import { useState } from 'react';
import { Plus, ArrowLeft, Loader2, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { SOCIAL_PLATFORMS } from './social-platforms';
import { normalizeSocialUrl, type SocialPlatform } from '@/lib/social-links';
import { cn } from '@/lib/utils';

const PLATFORM_MAX = 4;

interface SocialLinksCircleEditorProps {
  value: Record<string, string> | null;
  onChange: (val: Record<string, string>) => Promise<void> | void;
  saving?: boolean;
  readOnly?: boolean;
  size?: 'sm' | 'md';
}

export function SocialLinksCircleEditor({
  value,
  onChange,
  saving,
  readOnly = false,
  size = 'md',
}: SocialLinksCircleEditorProps) {
  const links = value ?? {};

  const [addOpen, setAddOpen] = useState(false);
  const [editKey, setEditKey] = useState<SocialPlatform | null>(null);
  const [isEditingLink, setIsEditingLink] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState<SocialPlatform | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [inputError, setInputError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const savedPlatforms = SOCIAL_PLATFORMS.filter((p) => links[p.key]?.trim());
  const availablePlatforms = SOCIAL_PLATFORMS.filter((p) => !links[p.key]?.trim());
  const canAddMore = availablePlatforms.length > 0 && savedPlatforms.length < PLATFORM_MAX;

  const circleSize = size === 'sm' ? 'h-8 w-8' : 'h-9 w-9';
  const iconSize = size === 'sm' ? 'h-3.5 w-3.5' : 'h-4 w-4';

  const closeAdd = () => {
    setAddOpen(false);
    setSelectedPlatform(null);
    setInputValue('');
    setInputError(null);
  };

  const closeEdit = () => {
    setEditKey(null);
    setIsEditingLink(false);
    setInputValue('');
    setInputError(null);
  };

  const handleSaveLink = async () => {
    if (!selectedPlatform) return;
    const result = normalizeSocialUrl(selectedPlatform, inputValue.trim());
    if (!result.ok) {
      setInputError(result.error);
      return;
    }
    setIsSaving(true);
    try {
      await onChange({ ...links, [selectedPlatform]: result.url });
      closeAdd();
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdateLink = async () => {
    if (!editKey) return;
    const result = normalizeSocialUrl(editKey, inputValue.trim());
    if (!result.ok) {
      setInputError(result.error);
      return;
    }
    setIsSaving(true);
    try {
      await onChange({ ...links, [editKey]: result.url });
      closeEdit();
    } finally {
      setIsSaving(false);
    }
  };

  if (readOnly) {
    if (savedPlatforms.length === 0) return null;
    return (
      <div className="flex items-center gap-2 flex-wrap">
        {savedPlatforms.map(({ key, label, icon: Icon, color }) => {
          const url = links[key];
          return (
            <a
              key={key}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              title={label}
              className={cn(
                'rounded-full border-2 flex items-center justify-center transition-all hover:scale-110 hover:shadow-md',
                circleSize
              )}
              style={{ borderColor: color + '45', backgroundColor: color + '12' }}
            >
              <Icon className={iconSize} style={{ color }} />
              <span className="sr-only">{label}</span>
            </a>
          );
        })}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {/* Saved platform circles */}
      {savedPlatforms.map(({ key, label, icon: Icon, color }) => (
        <Popover
          key={key}
          open={editKey === key}
          onOpenChange={(open) => {
            if (open) {
              setEditKey(key);
              setIsEditingLink(false);
              setInputValue(links[key] || '');
              setInputError(null);
              return;
            }
            closeEdit();
          }}
        >
          <PopoverTrigger asChild>
            <button
              type="button"
              title={label}
              className={cn(
                'rounded-full border-2 flex items-center justify-center transition-all hover:scale-110 hover:shadow-md active:scale-95',
                circleSize
              )}
              style={{ borderColor: color + '55', backgroundColor: color + '14' }}
            >
              <Icon className={iconSize} style={{ color }} />
            </button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            sideOffset={8}
            className="w-64 p-3.5 rounded-2xl shadow-xl border-border/50 bg-background"
          >
            {isEditingLink ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setIsEditingLink(false);
                      setInputValue(links[key] || '');
                      setInputError(null);
                    }}
                    className="text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <ArrowLeft className="h-4 w-4" />
                  </button>
                  <div
                    className="h-7 w-7 rounded-full flex items-center justify-center shrink-0"
                    style={{ backgroundColor: color + '18' }}
                  >
                    <Icon className="h-3.5 w-3.5" style={{ color }} />
                  </div>
                  <p className="text-sm font-bold text-foreground">Edit {label}</p>
                </div>
                <Input
                  type="text"
                  placeholder={`${label} URL or username`}
                  value={inputValue}
                  autoFocus
                  onChange={(e) => {
                    setInputValue(e.target.value);
                    setInputError(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') void handleUpdateLink();
                  }}
                  className="rounded-xl text-sm"
                />
                {inputError ? (
                  <p className="text-xs text-destructive font-medium">{inputError}</p>
                ) : null}
                <Button
                  type="button"
                  size="sm"
                  onClick={() => void handleUpdateLink()}
                  disabled={!inputValue.trim() || isSaving || saving}
                  className="w-full rounded-xl h-9 text-xs font-bold"
                >
                  {isSaving || saving ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                  ) : null}
                  Save
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center gap-2.5">
                  <div
                    className="h-8 w-8 rounded-full flex items-center justify-center shrink-0"
                    style={{ backgroundColor: color + '18' }}
                  >
                    <Icon className="h-4 w-4" style={{ color }} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-foreground">{label}</p>
                    <p className="text-[10px] text-muted-foreground truncate max-w-[150px]">
                      {links[key]}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <a
                    href={links[key]}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 text-center text-xs font-bold py-1.5 px-3 rounded-xl border border-primary/30 text-primary bg-primary/5 hover:bg-primary/10 transition-colors"
                  >
                    Visit
                  </a>
                  <button
                    type="button"
                    onClick={() => {
                      setIsEditingLink(true);
                      setInputValue(links[key] || '');
                      setInputError(null);
                    }}
                    className="flex-1 inline-flex items-center justify-center gap-1 text-center text-xs font-bold py-1.5 px-3 rounded-xl border border-primary/30 text-primary bg-primary/5 hover:bg-primary/10 transition-colors"
                  >
                    <Pencil className="h-3 w-3" />
                    Edit
                  </button>
                </div>
              </div>
            )}
          </PopoverContent>
        </Popover>
      ))}

      {/* Add "+" button */}
      {canAddMore && (
        <Popover open={addOpen} onOpenChange={(open) => (open ? setAddOpen(true) : closeAdd())}>
          <PopoverTrigger asChild>
            <button
              type="button"
              title="Add social link"
              className={cn(
                'rounded-full border-2 border-dashed border-primary/40 flex items-center justify-center text-primary/60 hover:text-primary hover:border-primary hover:bg-primary/5 transition-all hover:scale-110 active:scale-95',
                circleSize
              )}
            >
              <Plus className={iconSize} />
            </button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            sideOffset={8}
            className="w-72 p-4 rounded-2xl shadow-xl border-border/50 bg-background"
          >
            {!selectedPlatform ? (
              <div className="space-y-3">
                <p className="text-sm font-bold text-foreground">Add social link</p>
                <div className="grid grid-cols-2 gap-2">
                  {availablePlatforms.map(({ key, label, icon: Icon, color }) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => {
                        setSelectedPlatform(key);
                        setInputValue('');
                        setInputError(null);
                      }}
                      className="flex items-center gap-2 p-2.5 rounded-xl border border-border/50 hover:border-primary/30 hover:bg-primary/5 transition-all active:scale-95 text-left"
                    >
                      <div
                        className="h-7 w-7 rounded-full flex items-center justify-center shrink-0"
                        style={{ backgroundColor: color + '18' }}
                      >
                        <Icon className="h-3.5 w-3.5" style={{ color }} />
                      </div>
                      <span className="text-xs font-semibold text-foreground">{label}</span>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              (() => {
                const platform = SOCIAL_PLATFORMS.find((p) => p.key === selectedPlatform)!;
                return (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedPlatform(null);
                          setInputValue('');
                          setInputError(null);
                        }}
                        className="text-muted-foreground hover:text-foreground transition-colors"
                      >
                        <ArrowLeft className="h-4 w-4" />
                      </button>
                      <div
                        className="h-7 w-7 rounded-full flex items-center justify-center shrink-0"
                        style={{ backgroundColor: platform.color + '18' }}
                      >
                        <platform.icon className="h-3.5 w-3.5" style={{ color: platform.color }} />
                      </div>
                      <p className="text-sm font-bold text-foreground">{platform.label}</p>
                    </div>
                    <Input
                      type="text"
                      placeholder={`${platform.label} URL or username`}
                      value={inputValue}
                      autoFocus
                      onChange={(e) => {
                        setInputValue(e.target.value);
                        setInputError(null);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') void handleSaveLink();
                      }}
                      className="rounded-xl text-sm"
                    />
                    {inputError && (
                      <p className="text-xs text-destructive font-medium">{inputError}</p>
                    )}
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => void handleSaveLink()}
                      disabled={!inputValue.trim() || isSaving || saving}
                      className="w-full rounded-xl h-9 text-xs font-bold"
                    >
                      {isSaving || saving ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                      ) : null}
                      Save
                    </Button>
                  </div>
                );
              })()
            )}
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}
